import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const languages = [
  { code: 'EN', name: 'English', gtCode: 'en' },
  { code: 'SV', name: 'Svenska', gtCode: 'sv' },
  { code: 'ES', name: 'Español', gtCode: 'es' },
  { code: 'DE', name: 'Deutsch', gtCode: 'de' },
] as const;

const LANGUAGE_STORAGE_KEY = 'learnweb_selected_language';
const GOOGLE_TRANSLATE_ELEMENT_ID = 'google_translate_element';
const GOOGLE_TRANSLATE_SCRIPT_ID = 'google-translate-script';

type Language = (typeof languages)[number];

declare global {
  interface Window {
    googleTranslateElementInit?: () => void;
    google?: {
      translate?: {
        TranslateElement?: new (
          options: { pageLanguage: string; includedLanguages: string; autoDisplay: boolean },
          element: string
        ) => void;
      };
    };
  }
}

const getStoredLanguage = (): Language => {
  const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return languages.find((language) => language.code === storedLanguage) || languages[0];
};

const getCurrentGoogleTranslateCode = (): string | undefined => {
  return document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith('googtrans='))
    ?.split('/')
    .pop();
};

const getTranslatedLanguage = (): Language => {
  const translatedCode = getCurrentGoogleTranslateCode();
  return languages.find((language) => language.gtCode === translatedCode) || getStoredLanguage();
};

const ensureGoogleTranslateElement = () => {
  if (!document.getElementById(GOOGLE_TRANSLATE_ELEMENT_ID)) {
    const container = document.createElement('div');
    container.id = GOOGLE_TRANSLATE_ELEMENT_ID;
    container.style.display = 'none';
    document.body.appendChild(container);
  }
};

const loadGoogleTranslate = () => {
  ensureGoogleTranslateElement();

  window.googleTranslateElementInit = () => {
    if (!window.google?.translate?.TranslateElement) {
      return;
    }

    new window.google.translate.TranslateElement(
      {
        pageLanguage: 'en',
        includedLanguages: languages.map((language) => language.gtCode).join(','),
        autoDisplay: false,
      },
      GOOGLE_TRANSLATE_ELEMENT_ID
    );
  };

  if (window.google?.translate?.TranslateElement) {
    window.googleTranslateElementInit();
    return;
  }

  if (document.getElementById(GOOGLE_TRANSLATE_SCRIPT_ID)) {
    return;
  }

  const script = document.createElement('script');
  script.id = GOOGLE_TRANSLATE_SCRIPT_ID;
  script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
  script.async = true;
  document.body.appendChild(script);
};

const LanguageSelector: React.FC<{ className?: string }> = ({ className = '' }) => {
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState<Language['code']>(() => getTranslatedLanguage().code);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pendingGoogleTranslateCodeRef = useRef<string | null>(null);

  const selectedLanguage = languages.find((language) => language.code === selectedLang) || languages[0];

  const changeLanguage = (gtCode: string, attempt = 0) => {
    const select = document.querySelector<HTMLSelectElement>('.goog-te-combo');

    if (!select) {
      if (attempt < 20) {
        window.setTimeout(() => changeLanguage(gtCode, attempt + 1), 250);
      }
      return;
    }

    select.value = gtCode;
    select.dispatchEvent(new Event('change', { bubbles: true }));

    if (getCurrentGoogleTranslateCode() === gtCode) {
      pendingGoogleTranslateCodeRef.current = null;
      return;
    }

    if (attempt < 20) {
      window.setTimeout(() => changeLanguage(gtCode, attempt + 1), 250);
    } else {
      pendingGoogleTranslateCodeRef.current = null;
    }
  };

  const handleLangChange = (lang: Language) => {
    setSelectedLang(lang.code);
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lang.code);
    pendingGoogleTranslateCodeRef.current = lang.gtCode;
    loadGoogleTranslate();
    changeLanguage(lang.gtCode);
    setIsLangOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsLangOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      ref={dropdownRef}
      className={`relative notranslate ${className}`}
      translate="no"
    >
      <button
        type="button"
        onClick={() => setIsLangOpen((open) => !open)}
        className="flex items-center justify-between gap-2 min-w-[104px] px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-200 border border-slate-700 rounded-full hover:border-red-600 hover:text-white hover:bg-slate-900 transition-all duration-200"
        aria-expanded={isLangOpen}
        aria-haspopup="listbox"
      >
        <span className="flex items-center gap-2">
          <svg
            className="h-4 w-4 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3a9 9 0 100 18 9 9 0 000-18z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.6 9h16.8M3.6 15h16.8M12 3c2.25 2.47 3.38 5.47 3.38 9S14.25 18.53 12 21c-2.25-2.47-3.38-5.47-3.38-9S9.75 5.47 12 3z" />
          </svg>
          <span>{selectedLanguage.code}</span>
        </span>
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${isLangOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isLangOpen && (
        <div
          className="absolute right-0 top-full mt-3 w-44 overflow-hidden rounded-xl border border-slate-800 bg-slate-950 shadow-2xl"
          role="listbox"
          aria-label="Select language"
        >
          {languages.map((language) => (
            <button
              key={language.code}
              type="button"
              onClick={() => handleLangChange(language)}
              className={`flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold transition-colors ${
                selectedLang === language.code
                  ? 'bg-red-900/40 text-red-400'
                  : 'text-slate-300 hover:bg-slate-900 hover:text-white'
              }`}
              role="option"
              aria-selected={selectedLang === language.code}
            >
              <span>{language.name}</span>
              <span className="text-xs font-bold uppercase tracking-wider">{language.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const location = useLocation();

  const navLinks = [
    { to: '/', label: 'Home' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-slate-950/80 backdrop-blur-lg border-b border-slate-800/50 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">

    
{/* Logo with Claim Assessment Document Icon & Gradient Text */}
          <Link to="/" className="flex items-center gap-[5px] group">
            <svg 
              className="w-10 h-10 transition-transform duration-500 ease-out group-hover:-translate-y-1" 
              viewBox="0 0 100 100" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="logoPremiumGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#b91c1c" />
                  <stop offset="100%" stopColor="#ef4444" />
                </linearGradient>
                <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                  <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#ef4444" floodOpacity="0.5"/>
                </filter>
              </defs>
              
              {/* The Document Base */}
              <path 
                d="M30 15 H60 L80 35 V80 A5 5 0 0 1 75 85 H30 A5 5 0 0 1 25 80 V20 A5 5 0 0 1 30 15 Z" 
                className="fill-slate-900 stroke-white group-hover:stroke-white-600 transition-colors duration-300" 
                strokeWidth="4" 
                strokeLinejoin="round"
              />
              
              {/* The Folded Corner of the Document */}
              <path 
                d="M60 15 V35 H80" 
                className="stroke-white group-hover:stroke-white transition-colors duration-300" 
                fill="none" 
                strokeWidth="4" 
                strokeLinejoin="round"
              />
              
              {/* Abstract Lines representing the Assessment Form */}
              <line x1="38" y1="35" x2="48" y2="35" className="stroke-white" strokeWidth="4" strokeLinecap="round"/>
              <line x1="38" y1="48" x2="68" y2="48" className="stroke-white" strokeWidth="4" strokeLinecap="round"/>
              <line x1="38" y1="61" x2="55" y2="61" className="stroke-white" strokeWidth="4" strokeLinecap="round"/>

              {/* The "Trusted Option" Checkmark popping out */}
              <path 
                d="M30 65 L45 80 L90 35" 
                stroke="url(#logoPremiumGradient)" 
                strokeWidth="8" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                filter="url(#glow)"
                className="transform origin-center group-hover:scale-110 transition-transform duration-300"
              />
            </svg>
            <span className="text-2xl font-extrabold tracking-tighter text-white uppercase">
              Trusted<span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-red-600">Options</span>
            </span>
          </Link>



          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <div className="flex items-center gap-8 mr-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`relative text-xs font-bold uppercase tracking-widest py-2 group transition-colors duration-200 ${
                    isActive(link.to) ? 'text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {link.label}
                  {/* Animated Underline */}
                  <span 
                    className={`absolute bottom-0 left-0 w-full h-[2px] bg-red-600 transform origin-left transition-transform duration-300 ease-out ${
                      isActive(link.to) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                    }`}
                  ></span>
                </Link>
              ))}
            </div>

            <LanguageSelector />

            {/* Glowing Pill CTA Button */}
            <Link
              to={{ pathname: '/', hash: '#claim-form' }}
              onClick={(e: any) => {
                if (location.pathname === '/') {
                  e.preventDefault();
                  const el = document.getElementById('claim-form');
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  } else {
                    window.scrollTo({ top: window.innerHeight / 2, behavior: 'smooth' });
                  }
                }
              }}
              className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-6 py-3 bg-gradient-to-r from-red-700 to-red-600 text-white rounded-full hover:from-red-600 hover:to-red-500 shadow-[0_4px_14px_0_rgba(220,38,38,0.39)] hover:shadow-[0_6px_20px_rgba(220,38,38,0.5)] transform hover:-translate-y-0.5 transition-all duration-300"
            >
              Start Free Enquiry
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
              </svg>
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-slate-300 hover:text-white p-2.5 rounded-full hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-slate-950"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-slate-950 backdrop-blur-xl border-t border-slate-800/50 px-4 pb-6 pt-4 space-y-2 shadow-2xl absolute w-full">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block px-5 py-3.5 rounded-xl text-sm font-bold tracking-wide transition-all duration-200 ${
                isActive(link.to)
                  ? 'bg-gradient-to-r from-red-900/40 to-transparent text-red-500 border-l-2 border-red-500'
                  : 'text-slate-300 hover:bg-slate-900 hover:text-white border-l-2 border-transparent'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="pt-2">
            <LanguageSelector className="w-full [&>button]:w-full [&>button]:rounded-xl" />
          </div>
            <div className="pt-4 pb-2">
            <Link
              to={{ pathname: '/', hash: '#claim-form' }}
              onClick={(e: any) => {
                setIsMobileMenuOpen(false);
                if (location.pathname === '/') {
                  e.preventDefault();
                  const el = document.getElementById('claim-form');
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  } else {
                    window.scrollTo({ top: window.innerHeight / 2, behavior: 'smooth' });
                  }
                }
              }}
              className="flex justify-center items-center gap-2 w-full px-5 py-3.5 bg-gradient-to-r from-red-700 to-red-600 text-white rounded-xl text-sm font-bold tracking-wider hover:from-red-600 hover:to-red-500 shadow-lg transition-all duration-200"
            >
              Start Free Enquiry
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
              </svg>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
